// A run's broadcast attributes (RunDetailSerializer) carry everything the Images library
// row (ImageLibrarySerializer) renders, plus the counters that start at zero for a new run.
const buildLibraryRow = (attributes) => ({
  id: String(attributes.id),
  type: 'generatorRuns',
  attributes: {
    id: attributes.id,
    slug: attributes.slug,
    status: attributes.status,
    prompt: attributes.prompt,
    private: attributes.private,
    createdAt: attributes.createdAt,
    category: attributes.category,
    subCategory: attributes.subCategory,
    title: attributes.title,
    outputAssets: attributes.outputAssets || [],
    likeCount: 0,
    commentsCount: 0,
  },
})

/**
 * Puts a finished image run at the top of the cached Images library list and raises its
 * total count, so the library shows it without a fetch (the index can lag the broadcast
 * by a few seconds). A row the list already holds (a processing run loaded earlier) is
 * replaced in place. Keyword searches are left alone. Does not refetch.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {{ attributes: object }} params - The run's broadcast attributes
 */
export function addImageRunToLibraryCache(queryClient, { attributes } = {}) {
  if (!queryClient?.setQueriesData || attributes?.id == null) {
    return
  }

  const row = buildLibraryRow(attributes)

  queryClient.setQueriesData(
    {
      predicate: (query) => Array.isArray(query.queryKey)
        && query.queryKey[0] === 'libraryItems'
        && query.queryKey[1] === 'images'
        && query.queryKey.length === 2,
    },
    (oldData) => {
      if (oldData == null || !Array.isArray(oldData.pages) || oldData.pages.length === 0) {
        return oldData
      }

      const isAlreadyListed = oldData.pages.some(page => (
        (page.items || []).some(item => item != null && String(item.id) === row.id)
      ))

      if (isAlreadyListed) {
        return {
          ...oldData,
          pages: oldData.pages.map(page => ({
            ...page,
            items: (page.items || []).map(item => (item != null && String(item.id) === row.id ? row : item)),
          })),
        }
      }

      return {
        ...oldData,
        pages: oldData.pages.map((page, index) => ({
          ...page,
          items: index === 0 ? [row, ...(page.items || [])] : page.items,
          ...(typeof page.meta?.totalCount === 'number' && {
            meta: { ...page.meta, totalCount: page.meta.totalCount + 1 },
          }),
        })),
      }
    },
  )
}
